import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { dateTime, money } from "@/lib/format";
import type { AdminWallet, LedgerEntry, TransactionKind } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Pager, Select, Skeleton, Stat } from "@/components/ui";
import { LedgerTable } from "@/components/LedgerTable";

const PAGE_SIZE = 25;

/**
 * The operator's view of one customer's money.
 *
 * Everything here writes a ledger entry: there is no field that edits a balance
 * directly, because a balance somebody typed cannot be explained a year later.
 * Corrections are reversals, which is why the table offers Reverse rather than
 * Edit or Delete.
 */
export function AccountWallet() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [form, setForm] = useState({ kind: "", amount: "", description: "", reference: "" });
  const [reversing, setReversing] = useState<LedgerEntry | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-wallet", id, offset],
    queryFn: () => api.get<AdminWallet>(`/v1/admin/accounts/${id}/wallet?limit=${PAGE_SIZE}&offset=${offset}`),
    placeholderData: keepPreviousData,
  });

  const { data: kinds } = useQuery({
    queryKey: ["transaction-kinds"],
    queryFn: () => api.get<{ data: TransactionKind[] }>("/v1/admin/transaction-kinds"),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-wallet", id] });
    queryClient.invalidateQueries({ queryKey: ["platform-accounts"] });
  };

  const post = useMutation({
    mutationFn: () =>
      api.post<LedgerEntry>(`/v1/admin/accounts/${id}/transactions`, {
        kind: form.kind,
        amount_cents: Math.round(Number(form.amount) * 100),
        description: form.description,
        reference: form.reference || undefined,
      }),
    onMutate: () => setError(""),
    onSuccess: () => {
      setForm({ kind: "", amount: "", description: "", reference: "" });
      refresh();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not post that transaction."),
  });

  const reverse = useMutation({
    mutationFn: () =>
      api.post<LedgerEntry>(`/v1/admin/accounts/${id}/transactions/${reversing?.id}/reverse`, { reason }),
    onMutate: () => setError(""),
    onSuccess: () => {
      setReversing(null);
      setReason("");
      refresh();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not reverse that transaction."),
  });

  if (isLoading && !data) {
    return (
      <Card>
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  const wallet = data?.wallet;
  const selected = kinds?.data.find((k) => k.kind === form.kind);

  return (
    <>
      <PageHeader
        title={data?.account.trading_name || data?.account.legal_name || "Account"}
        subtitle="Wallet, transactions and manual adjustments. Everything here is logged against your name."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-4">
        <Stat
          label="Balance"
          value={money(wallet?.balance_cents)}
          tone={(wallet?.balance_cents ?? 0) < 0 ? "bad" : "neutral"}
        />
        <Stat label="Credit limit" value={money(wallet?.credit_limit_cents)} />
        <Stat label="Spent this month" value={money(wallet?.spend_mtd_cents)} />
        <Stat label="Topped up this month" value={money(wallet?.topped_up_mtd_cents)} />
      </div>

      {error && (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {reversing && (
        <Card className="mb-5 border-accent-strong/30 bg-accent-soft">
          <h2 className="text-lg font-bold tracking-tight">
            Reverse {reversing.kind_label} of {money(reversing.amount_cents)}
          </h2>
          <p className="mt-1 text-sm text-ink-700">
            This writes an opposite entry. The original stays on the statement, marked reversed, so
            the customer can see what happened rather than finding a row missing.
          </p>
          <Field
            label="Reason"
            className="mt-4 max-w-lg"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Credited to the wrong account"
          />
          <div className="mt-4 flex gap-2">
            <Button loading={reverse.isPending} disabled={reason.trim().length < 4} onClick={() => reverse.mutate()}>
              Reverse transaction
            </Button>
            <Button variant="secondary" onClick={() => setReversing(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <Card className="mb-6">
        <h2 className="text-lg font-bold tracking-tight">Add a transaction</h2>
        <p className="mb-4 mt-1 text-sm text-ink-500">
          Shipment charges and top-ups are not here on purpose: those are created by the booking flow
          and the payment gateway, so the money always points at the thing that moved it.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="Type"
            value={form.kind}
            onChange={(event) => setForm({ ...form, kind: event.target.value })}
            options={[
              { value: "", label: "Select" },
              ...(kinds?.data ?? []).map((k) => ({ value: k.kind, label: k.label })),
            ]}
          />
          <Field
            label="Amount (R)"
            type="number"
            min={0}
            value={form.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
          />
          <Field
            label="Waybill or reference"
            value={form.reference}
            onChange={(event) => setForm({ ...form, reference: event.target.value })}
            placeholder="Optional"
          />
          <Field
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            placeholder="Goodwill after a late delivery"
            hint="Appears on the customer's statement."
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            loading={post.isPending}
            disabled={!form.kind || !form.amount || form.description.trim().length < 4}
            onClick={() => post.mutate()}
          >
            Post transaction
          </Button>
          {selected && form.amount && (
            <p className="text-sm text-ink-500">
              New balance{" "}
              <span className="font-semibold text-ink-900">
                {money(
                  (wallet?.balance_cents ?? 0) +
                    (selected.direction === "credit" ? 1 : -1) * Math.round(Number(form.amount) * 100),
                )}
              </span>
            </p>
          )}
        </div>
      </Card>

      {!!data?.payments.length && (
        <>
          <h2 className="mb-3 text-lg font-bold tracking-tight">Recent payments</h2>
          <Card className="mb-6 divide-y divide-ink-100 p-0">
            {data.payments.map((payment) => (
              <div key={payment.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="font-semibold">{money(payment.amount_cents)}</span>
                <Badge
                  tone={
                    payment.status === "succeeded" ? "good" : payment.status === "pending" ? "attention" : "bad"
                  }
                >
                  {payment.status}
                </Badge>
                <span className="text-sm text-ink-500">
                  {payment.provider}
                  {payment.method ? `, ${payment.method}` : ""}
                </span>
                {payment.failure_reason && (
                  <span className="text-sm text-signal-bad">{payment.failure_reason}</span>
                )}
                <span className="ml-auto text-sm text-ink-400">{dateTime(payment.created_at)}</span>
              </div>
            ))}
          </Card>
        </>
      )}

      <h2 className="mb-3 text-lg font-bold tracking-tight">Ledger</h2>
      {!data?.entries.length ? (
        <Card>
          <p className="text-sm text-ink-500">No transactions on this account yet.</p>
        </Card>
      ) : (
        <>
          <LedgerTable
            entries={data.entries}
            canReverse
            onReverse={(entry) => {
              setReversing(entry);
              setReason("");
            }}
          />
          <Pager total={data.meta.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
        </>
      )}
    </>
  );
}
